import { useCallback, useContext, useEffect, useState } from 'react';

// Contexts
import MultisportApiContext from '../contexts/MultisportApiContext';

import { IMultisportCompetitionEvents, IMultisportEventsConfig, ISubscriberFn, SportTypes } from '../../../../packages/types';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';

import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import { FULL_NAME, NAME, SHORT_NAME, SHORT_NAME_ALT, THREE_LETTER_CODE_ALT } from '../../../../packages/constants/nameTypes';

interface IUseEventsArgs extends IGeneralSubscriberInfo {
  date: string;
  sport?: SportTypes;
  competitionIds?: string;
  dataOddsDisplay: IMultisportEventsConfig['dataOddsDisplay'];
  dataOddsBettingId: IMultisportEventsConfig['dataOddsBettingId'];
  dataOddsPreEventOnly: IMultisportEventsConfig['dataOddsPreEventOnly'];
  dataTeamNameType: IMultisportEventsConfig['dataTeamNameType'];
  dataWidgetSport?: string;
  dataWidgetId?: string;
  dataWidgetCuid?: string;
  subscribers?: { [key: string]: ISubscriberFn[] };
  dataRefreshTime?: IMultisportEventsConfig['dataRefreshTime'];
  showTopEventsAndCompetitionsOptions?: boolean;
}

export const modifyNameTypeForSdk = (nameType?: string) => {
  switch (nameType) {
    case FULL_NAME:
      return NAME;
    case SHORT_NAME:
      return SHORT_NAME_ALT;
    default:
      return THREE_LETTER_CODE_ALT;
  }
};

export default function useEvents({
  date,
  sport,
  competitionIds,
  dataOddsDisplay,
  dataOddsBettingId,
  dataOddsPreEventOnly,
  dataTeamNameType,
  dataWidgetSport,
  dataWidgetCuid,
  dataWidgetId,
  subscribers,
  dataRefreshTime,
  showTopEventsAndCompetitionsOptions,
}: IUseEventsArgs) {
  const { $multisport } = useContext(MultisportApiContext);
  const [competitionEvents, setCompetitionEvents] = useState<IMultisportCompetitionEvents>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchCompetitionEvents = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      if (showTopEventsAndCompetitionsOptions) {
        manageLoader && setIsLoading(true);
        $multisport
          ?.events({
            sport,
            date,
            competitionIds,
            nameType: modifyNameTypeForSdk(dataTeamNameType),
            dataOddsDisplay,
            dataOddsBettingId,
            dataOddsPreEventOnly,
          })
          ?.then((res) => {
            manageLoader && setIsLoading(false);
            if (res) {
              setCompetitionEvents(res);
              !isFromInterval && stackOdds(res, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
            }
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [
      $multisport,
      sport,
      date,
      competitionIds,
      dataOddsDisplay,
      dataOddsBettingId,
      dataOddsPreEventOnly,
      dataTeamNameType,
      showTopEventsAndCompetitionsOptions,
    ],
  );

  const refetchWidgetData = useFetchIntervalData(fetchCompetitionEvents, dataRefreshTime);

  useEffect(() => {
    fetchCompetitionEvents(true);
  }, [fetchCompetitionEvents]);

  return {
    competitionEvents,
    refetchWidgetData,
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
    isLoading,
    errorMessage,
  };
}
